import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { runAshevilleSimulation } from '../utils/ashevilleSimulator';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

const BackroomScans = () => {
  const [selectedStore, setSelectedStore] = useState('');
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadedScans, setUploadedScans] = useState([]);

  // Asheville Simulation State
  const [simulationResults, setSimulationResults] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [simulationError, setSimulationError] = useState(null);

  // Platform/Store options
  const storeOptions = [
    'ATL', 'BNA', 'ASH', 'FLL', 'JAX', 'SRQ', 'TPA', 'MIA', 'MCO', 'SAV', 'CHS'
  ];

  // Load uploaded PDFs from database on component mount
  useEffect(() => {
    loadPdfs();
  }, []);

  const loadPdfs = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
      const response = await fetch(`${API_BASE}/api/space/pdfs`);
      if (response.ok) {
        const data = await response.json();
        setUploadedScans(data);
        console.log('📋 Loaded PDFs from database:', data.length);
      }
    } catch (error) {
      console.error('Error loading PDFs:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedStore) {
      setUploadMessage({ type: 'error', text: 'Please select a store/platform first' });
      return;
    }

    if (file.type !== 'application/pdf') {
      setUploadMessage({ type: 'error', text: 'Please upload PDF files only' });
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB limit
    if (file.size > maxSize) {
      setUploadMessage({ type: 'error', text: 'File size must be under 10MB' });
      return;
    }

    // Upload to database via API
    const uploadPdf = async () => {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('uploadedBy', 'Space User'); // Could be enhanced with actual user info
      formData.append('warehouseId', selectedStore);

      try {
        const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
        const response = await fetch(`${API_BASE}/api/space/upload-pdf`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📄 PDF uploaded successfully:', result);
          
          // Refresh PDF list
          await loadPdfs();
          
          setUploadMessage({ 
            type: 'success', 
            text: `Successfully uploaded ${file.name} for ${selectedStore}` 
          });
          
          // Clear form
          setSelectedStore('');
          event.target.value = '';
        } else {
          const error = await response.json();
          setUploadMessage({ 
            type: 'error', 
            text: `Upload failed: ${error.error}` 
          });
        }
      } catch (error) {
        console.error('Error uploading PDF:', error);
        setUploadMessage({ 
          type: 'error', 
          text: 'Network error during upload' 
        });
      }
    };

    uploadPdf();
  };

  const handleViewPDF = (scan) => {
    // Open PDF directly from server URL
    const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
    const pdfUrl = `${API_BASE}${scan.file_path}`;
    window.open(pdfUrl, '_blank');
  };

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this PDF scan?')) return;
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
      const response = await fetch(`${API_BASE}/api/space/pdf/${scanId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        console.log('🗑️ PDF deleted successfully');
        // Refresh PDF list
        await loadPdfs();
        setUploadMessage({ type: 'success', text: 'Scan deleted successfully' });
      } else {
        const error = await response.json();
        setUploadMessage({ type: 'error', text: `Delete failed: ${error.error}` });
      }
    } catch (error) {
      console.error('Error deleting PDF:', error);
      setUploadMessage({ type: 'error', text: 'Network error during deletion' });
    }
  };

  const formatFileSize = (size) => {
    return size;
  };

  // Asheville Simulation Functions
  const handleRunSimulation = async () => {
    setSimulationLoading(true);
    setSimulationError(null);
    
    try {
      console.log('Starting Asheville delivery optimization simulation...');
      const results = await runAshevilleSimulation();
      setSimulationResults(results);
      console.log('Simulation completed successfully');
    } catch (error) {
      console.error('Simulation failed:', error);
      setSimulationError('Simulation failed: ' + error.message);
    } finally {
      setSimulationLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Page Header */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Backroom Scans Management
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Upload and manage PDF backroom layout scans for each store/platform
          </Typography>
        </Grid>

        {/* Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Upload New Scan
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Store/Platform</InputLabel>
                  <Select
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                    label="Store/Platform"
                  >
                    {storeOptions.map(store => (
                      <MenuItem key={store} value={store}>{store}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={!selectedStore}
                  sx={{ width: '100%', py: 2 }}
                >
                  Upload PDF Backroom Scan
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </Button>
              </Grid>

              {uploadMessage && (
                <Grid item xs={12}>
                  <Alert severity={uploadMessage.type} onClose={() => setUploadMessage(null)}>
                    {uploadMessage.text}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* View Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Uploaded Scans ({uploadedScans.length})
            </Typography>
            
            {uploadedScans.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PdfIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  No backroom scans uploaded yet
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadedScans
                      .sort((a, b) => new Date(b.uploaded_at || b.uploadDate) - new Date(a.uploaded_at || a.uploadDate))
                      .map((scan) => (
                        <TableRow key={scan.id}>
                          <TableCell>
                            <Chip 
                              label={scan.warehouse_id || scan.store || 'Unknown'} 
                              size="small" 
                              color="primary" 
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {scan.original_name || scan.fileName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {scan.uploaded_at ? 
                              new Date(scan.uploaded_at).toISOString().split('T')[0] : 
                              scan.uploadDate
                            }
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {scan.file_size ? 
                              (scan.file_size / 1024 / 1024).toFixed(2) + ' MB' : 
                              scan.fileSize
                            }
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleViewPDF(scan)}
                              sx={{ mr: 1 }}
                            >
                              <ViewIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteScan(scan.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Asheville Delivery Optimization Simulator */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Asheville Delivery Optimization Simulator
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Analyze different delivery schedules for Asheville to optimize cost vs in-stock rate.
              Simulates 90 days with backroom allocation and MIN/MAX recommendations.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleRunSimulation}
                disabled={simulationLoading}
                sx={{ mr: 2 }}
              >
                {simulationLoading ? 'Running Simulation...' : 'Run ASH Simulation'}
              </Button>
              
              {simulationResults && (
                <Button
                  variant="outlined"
                  onClick={() => setSimulationResults(null)}
                >
                  Clear Results
                </Button>
              )}
            </Box>

            {simulationError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {simulationError}
              </Alert>
            )}

            {simulationResults && (
              <>
                {/* Scenario Comparison Table */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                  Scenario Comparison
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Scenario</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Deliveries/Week</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Cost (90 days)</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cost/Week</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>In-Stock Rate</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Backroom Used</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Stockouts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {simulationResults.scenarios.map((scenario, index) => (
                        <TableRow key={index} sx={{ 
                          backgroundColor: index === 0 ? '#e3f2fd' : 'inherit',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}>
                          <TableCell sx={{ fontWeight: 600 }}>{scenario.scenario}</TableCell>
                          <TableCell>{scenario.deliveriesPerWeek}</TableCell>
                          <TableCell>${scenario.totalCost.toLocaleString()}</TableCell>
                          <TableCell>${scenario.costPerWeek}</TableCell>
                          <TableCell sx={{ 
                            color: parseFloat(scenario.inStockRate) >= 95 ? '#4caf50' : 
                                   parseFloat(scenario.inStockRate) >= 90 ? '#ff9800' : '#f44336',
                            fontWeight: 600 
                          }}>
                            {scenario.inStockRate}
                          </TableCell>
                          <TableCell>{scenario.backroomUsage}</TableCell>
                          <TableCell>{scenario.totalStockouts}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Backroom Inventory Allocation */}
                {simulationResults.scenarios && simulationResults.scenarios[0] && simulationResults.scenarios[0].backroomItems && simulationResults.scenarios[0].backroomItems.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                      Backroom Inventory Allocation
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Items assigned to backroom storage space (beyond planogram floor capacity)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Daily Sales</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Planogram MAX</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Backroom Units</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Total MAX</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Space Used (sq ft)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {simulationResults.scenarios[0].backroomItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{item.sku}</TableCell>
                              <TableCell>{item.description}</TableCell>
                              <TableCell align="right">{item.dailySales.toFixed(2)}</TableCell>
                              <TableCell align="right">{item.planogramMax}</TableCell>
                              <TableCell align="right">{item.backroomAllocation}</TableCell>
                              <TableCell align="right">{item.max}</TableCell>
                              <TableCell align="right">{item.sqFtUsed.toFixed(1)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Top Velocity SKUs Table */}
                {simulationResults.topVelocitySkus && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                      Top 20 Velocity SKUs - Recommended MIN/MAX
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Based on "{simulationResults.scenarios[0]?.scenario}" scenario (recommended)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Daily Sales</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Planogram MAX</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Recommended MIN</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Recommended MAX</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Backroom Allocation</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {simulationResults.topVelocitySkus.map((sku, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{sku.sku}</TableCell>
                              <TableCell>{sku.description}</TableCell>
                              <TableCell align="right">{sku.salesPerDay.toFixed(2)}</TableCell>
                              <TableCell align="right">{sku.planogramMax}</TableCell>
                              <TableCell align="right" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                {simulationResults.scenarios[0]?.skuConfigs.find(s => s.sku === sku.sku)?.min || 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                {simulationResults.scenarios[0]?.skuConfigs.find(s => s.sku === sku.sku)?.max || 'N/A'}
                              </TableCell>
                              <TableCell align="right">
                                {simulationResults.scenarios[0]?.skuConfigs.find(s => s.sku === sku.sku)?.backroomAllocation || 0} units
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Simulation Parameters:</strong> 90-day period, $634 per delivery, 109.5 sq ft backroom, 
                        1-day lead time, 10 units/sq ft storage density
                      </Typography>
                    </Alert>
                  </>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
  );
};

export default BackroomScans;